import { useEffect, useMemo, useState } from 'react';
import { fetchRestaurantsByDistance } from '@/api/menus';
import { cartProductIds, filterSuitableRestaurantsForCart, hasDeliveryAddressFields, shouldShowDeliveryRestaurantPicker, } from '@/utils/deliveryRestaurants';
function applyLocalSuitableRestaurants(restaurants, cartItems, setPickupRestaurantId) {
    const suitable = filterSuitableRestaurantsForCart(restaurants, cartItems);
    setPickupRestaurantId((prev) => {
        if (prev != null && suitable.some((r) => r.id === prev))
            return prev;
        if (suitable.length === 1)
            return suitable[0].id;
        return null;
    });
    return { suitable, noSuitable: suitable.length === 0 };
}
export function useDeliverySuitableRestaurants(options) {
    const { deliveryType, checkoutMenuId, deliveryAddress, cartItems, restaurants, setPickupRestaurantId, suspended = false, } = options;
    const [sortedSuitableRestaurants, setSortedSuitableRestaurants] = useState([]);
    const [suitableRestaurantsLoading, setSuitableRestaurantsLoading] = useState(false);
    const [deliverySelectionWarning, setDeliverySelectionWarning] = useState(null);
    const [deliveryAddressGeocoded, setDeliveryAddressGeocoded] = useState(true);
    const [noSuitableRestaurants, setNoSuitableRestaurants] = useState(false);
    const showDeliveryRestaurantPicker = shouldShowDeliveryRestaurantPicker(deliveryType, restaurants.length);
    const userAddressReady = hasDeliveryAddressFields(deliveryAddress);
    useEffect(() => {
        if (suspended)
            return;
        if (!showDeliveryRestaurantPicker || checkoutMenuId == null) {
            setSortedSuitableRestaurants([]);
            setDeliverySelectionWarning(null);
            setDeliveryAddressGeocoded(true);
            setNoSuitableRestaurants(false);
            if (deliveryType === 'delivery' && restaurants.length === 1) {
                setPickupRestaurantId(restaurants[0].id);
            }
            return;
        }
        if (!userAddressReady || deliveryAddress == null) {
            const { suitable, noSuitable } = applyLocalSuitableRestaurants(restaurants, cartItems, setPickupRestaurantId);
            setSortedSuitableRestaurants(suitable);
            setDeliverySelectionWarning(null);
            setDeliveryAddressGeocoded(false);
            setNoSuitableRestaurants(noSuitable);
            return;
        }
        let cancelled = false;
        setSuitableRestaurantsLoading(true);
        const productIds = cartProductIds(cartItems);
        fetchRestaurantsByDistance(checkoutMenuId, deliveryAddress, productIds)
            .then((res) => {
            if (cancelled)
                return;
            setSortedSuitableRestaurants(res.restaurants);
            setDeliverySelectionWarning(res.selectionWarning ?? null);
            setDeliveryAddressGeocoded(res.addressGeocoded !== false);
            setNoSuitableRestaurants(res.restaurants.length === 0);
            setPickupRestaurantId((prev) => {
                if (prev != null && res.restaurants.some((r) => r.id === prev))
                    return prev;
                if (res.restaurants.length === 1)
                    return res.restaurants[0].id;
                if (res.addressGeocoded === false)
                    return null;
                return res.restaurants[0]?.id ?? null;
            });
        })
            .catch(() => {
            if (!cancelled) {
                const { suitable, noSuitable } = applyLocalSuitableRestaurants(restaurants, cartItems, setPickupRestaurantId);
                setSortedSuitableRestaurants(suitable);
                setDeliverySelectionWarning(null);
                setDeliveryAddressGeocoded(false);
                setNoSuitableRestaurants(noSuitable);
            }
        })
            .finally(() => {
            if (!cancelled)
                setSuitableRestaurantsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [
        cartItems,
        checkoutMenuId,
        deliveryAddress,
        deliveryType,
        restaurants,
        setPickupRestaurantId,
        showDeliveryRestaurantPicker,
        suspended,
        userAddressReady,
    ]);
    const requiresDeliveryRestaurantPick = useMemo(() => {
        if (!showDeliveryRestaurantPicker || sortedSuitableRestaurants.length <= 1)
            return false;
        if (!userAddressReady)
            return true;
        return !deliveryAddressGeocoded;
    }, [
        showDeliveryRestaurantPicker,
        sortedSuitableRestaurants.length,
        userAddressReady,
        deliveryAddressGeocoded,
    ]);
    return {
        sortedSuitableRestaurants,
        suitableRestaurantsLoading,
        deliverySelectionWarning,
        deliveryAddressGeocoded,
        noSuitableRestaurants,
        showDeliveryRestaurantPicker,
        requiresDeliveryRestaurantPick,
        hasDeliveryAddressFields: userAddressReady,
    };
}
